import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TeamLogo from '../atoms/TeamLogo';
import {
  COLOR_BORDER,
  COLOR_ORANGE,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_MIDDLE_GRAY,
  COLOR_TEXT,
  COLOR_WHITE,
  COLOR_YELLOW,
} from '../styles/colors';
import Player from '../types/Player';
import Team from '../types/Team';
import Dialog from './Dialog';
import FullGameLogTable from './FullGameLogTable';

export interface CollegeYearEntry {
  year: number;
  team?: Team;
  teamName: string;
  jerseyNumber?: string;
  starts: number;
  games: number;
  snaps: number;
  mostSnaps?: boolean;
}

interface CollegeCareerOverviewProps {
  className?: string;
  player?: Player;
  /** Career totals: starts, games, snaps */
  careerTotals?: { starts: number; games: number; snaps: number };
  /** Per-year breakdown. If not provided, demo data is used. */
  yearEntries?: CollegeYearEntry[];
  fullGameLogHref?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: theme.spacing(1),
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: COLOR_MIDDLE_GRAY,
      borderRadius: 4,
    },
  },
  scrollRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    minWidth: 'min-content',
    padding: theme.spacing(2, 0),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 0),
    },
  },
  card: {
    flexShrink: 0,
    minWidth: 200,
    maxWidth: 280,
    width: 260,
    backgroundColor: COLOR_WHITE,
    borderRadius: 12,
    border: `1px solid ${COLOR_BORDER}`,
    boxSizing: 'border-box',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      minWidth: 180,
      width: 220,
    },
  },
  summaryCard: {
    border: `2px solid ${COLOR_ORANGE}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    color: COLOR_TEXT,
  },
  yearHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  yearBadge: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    color: COLOR_TEXT,
  },
  teamLogo: {
    height: 24,
    width: 'auto',
  },
  teamLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: COLOR_TEXT,
  },
  fullGameLogLink: {
    fontSize: theme.typography.pxToRem(13),
    color: COLOR_BLUE,
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  cardBody: {
    display: 'flex',
    alignItems: 'stretch',
  },
  statColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2, 1),
    borderRight: `1px solid ${COLOR_BORDER}`,
    '&:last-child': {
      borderRight: 'none',
    },
  },
  statValue: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(22),
    color: COLOR_TEXT,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: COLOR_TEXT,
    marginTop: theme.spacing(0.25),
  },
  mostSnapsBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: COLOR_YELLOW,
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 700,
    padding: theme.spacing(0.25, 1),
    borderRadius: 8,
    boxShadow: `0 1px 4px ${COLOR_BORDER}`,
  },
  yearCardWrapper: {
    position: 'relative',
  },
  fullGameLogDialogContent: {
    maxWidth: 1100,
    width: '96%',
  },
}));

const DEFAULT_CAREER = { starts: 27, games: 24, snaps: 896 };

const DEFAULT_YEARS: CollegeYearEntry[] = [
  { year: 2025, teamName: 'Auburn', jerseyNumber: '45', starts: 10, games: 10, snaps: 344, mostSnaps: true },
  { year: 2024, teamName: 'Auburn', jerseyNumber: '45', starts: 9, games: 8, snaps: 352 },
  { year: 2023, teamName: 'Auburn', jerseyNumber: '45', starts: 8, games: 6, snaps: 200 },
  { year: 2023, teamName: 'Auburn', jerseyNumber: '45', starts: 8, games: 6, snaps: 200 },
  { year: 2023, teamName: 'Auburn', jerseyNumber: '45', starts: 8, games: 6, snaps: 200 },
  { year: 2023, teamName: 'Auburn', jerseyNumber: '45', starts: 8, games: 6, snaps: 200 },
  { year: 2023, teamName: 'Auburn', jerseyNumber: '45', starts: 8, games: 6, snaps: 200 },
];

export default function CollegeCareerOverview(props: CollegeCareerOverviewProps) {
  const {
    className,
    player,
    careerTotals,
    yearEntries,
    fullGameLogHref,
  } = props;

  const [isFullGameLogDialogOpen, setIsFullGameLogDialogOpen] = useState<boolean>(false);
  const classes = useStyles();

  const totals = careerTotals ?? (player ? {
    starts: player.startsCareer ?? 0,
    games: player.gamesCareer ?? 0,
    snaps: player.snapsCareer ?? 0,
  } : DEFAULT_CAREER);

  const years = yearEntries ?? DEFAULT_YEARS;

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.scrollRow}>
        {/* Summary card */}
        <div className={clsx(classes.card, classes.summaryCard)}>
          <div className={classes.cardHeader}>
            <span className={classes.cardTitle}>College Career</span>
            {fullGameLogHref != null ? (
              <a
                href={fullGameLogHref}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.fullGameLogLink}
              >
                Full Game Log
              </a>
            ) : (
              <span onClick={() => {setIsFullGameLogDialogOpen(true)}} className={classes.fullGameLogLink}>Full Game Log</span>
            )}
          </div>
          <div className={classes.cardBody}>
            <div className={classes.statColumn}>
              <span className={classes.statValue}>{totals.starts}</span>
              <span className={classes.statLabel}>Starts</span>
            </div>
            <div className={classes.statColumn}>
              <span className={classes.statValue}>{totals.games}</span>
              <span className={classes.statLabel}>Games</span>
            </div>
            <div className={classes.statColumn}>
              <span className={classes.statValue}>{totals.snaps}</span>
              <span className={classes.statLabel}>Snaps</span>
            </div>
          </div>
        </div>

        {/* Year cards */}
        {years.map((entry, idx) => (
          <div key={idx} className={classes.yearCardWrapper}>
            {entry.mostSnaps && (
              <span className={classes.mostSnapsBadge}>Most Snaps</span>
            )}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.yearHeader}>
                  <span className={classes.yearBadge}>{entry.year}</span>
                  {entry.team && (
                    <TeamLogo team={entry.team} className={classes.teamLogo} />
                  )}
                  <span className={classes.teamLabel}>
                    {entry.teamName}
                    {entry.jerseyNumber != null && entry.jerseyNumber !== '' && ` - #${entry.jerseyNumber}`}
                  </span>
                </div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.statColumn}>
                  <span className={classes.statValue}>{entry.starts}</span>
                  <span className={classes.statLabel}>Starts</span>
                </div>
                <div className={classes.statColumn}>
                  <span className={classes.statValue}>{entry.games}</span>
                  <span className={classes.statLabel}>Games</span>
                </div>
                <div className={classes.statColumn}>
                  <span className={classes.statValue}>{entry.snaps}</span>
                  <span className={classes.statLabel}>Snaps</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog
        open={isFullGameLogDialogOpen}
        title="Full Game Log"
        onClose={() => setIsFullGameLogDialogOpen(false)}
        containsLongContent
        contentClassName={classes.fullGameLogDialogContent}
      >
        <FullGameLogTable useNamedBox={false} />
      </Dialog>
    </div>
  );
}
